import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { ModalHeader } from '~/shared/components/ModalHeader';
import { toHtmlDate } from '~/shared/utils/toHtmlDate';
import { TravelPlans, WrittenReportNeeded } from '~/gql/graphql';
import {
  travelPlansLabel,
  writtenReportNeededLabel,
  type MatterViewModel,
  type MyMatterExpertsRow,
} from '~/modules/matters/matters.types';
import { getFirstHiredExpertId } from '~/modules/matters/matterExpertUtils';
import { MATTER_SIDE_OPTIONS, type MatterSide } from '~/shared/types/connect';

const schema = z.object({
  expertId: z.string().optional(),
  title: z.string().min(1, 'Matter title is required'),
  caseDescription: z.string().min(1, 'Brief case description is required'),
  caseNumber: z.string().optional(),
  courtName: z.string().optional(),
  location: z.string().optional(),
  keyDates: z.string().optional(),
  nextDeadline: z.string().optional(),
  travelPlans: z
    .union([
      z.enum([
        TravelPlans.Required,
        TravelPlans.Desired,
        TravelPlans.NotApplicable,
      ]),
      z.literal(''),
    ])
    .optional(),
  writtenReportNeeded: z
    .union([
      z.enum([
        WrittenReportNeeded.Yes,
        WrittenReportNeeded.No,
        WrittenReportNeeded.Maybe,
      ]),
      z.literal(''),
    ])
    .optional(),
  side: z
    .union([
      z.enum(MATTER_SIDE_OPTIONS as unknown as [MatterSide, ...MatterSide[]]),
      z.literal(''),
    ])
    .optional(),
});

export interface CreateMatterFormData extends z.infer<typeof schema> {}

/** GQL matter plus optional legacy connect-store fields (edit mode until store fully migrates). */
export type CreateMatterModalPrefillMatter = MatterViewModel & {
  description?: string;
  court?: string;
  expertIds?: string[];
  side?: MatterSide;
};

function getMatterSubtitle(options: {
  isEditMode: boolean;
  matter: CreateMatterModalPrefillMatter | undefined;
  initialExpertId: string | undefined;
  experts: ReadonlyArray<MyMatterExpertsRow>;
}): string {
  const { isEditMode, matter, initialExpertId, experts } = options;
  if (isEditMode && matter) {
    return `Edit details for ${matter.title}`;
  }
  if (initialExpertId) {
    const expert = experts.find((row) => row.id === initialExpertId);
    const name = expert?.name ?? 'this expert';
    return `Create a new matter with ${name}.`;
  }
  return 'Create a new matter to begin working with an expert witness.';
}

function getSubmitLabel(options: {
  isEditMode: boolean;
  isSubmitting: boolean;
}): string {
  const { isEditMode, isSubmitting } = options;
  if (isSubmitting) {
    return isEditMode ? 'Saving…' : 'Creating…';
  }
  return isEditMode ? 'Save Changes' : 'Create Matter';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Rows from `myMatterExperts` (or same shape); profile flow may merge with a full connect `User`. */
  experts: ReadonlyArray<MyMatterExpertsRow>;
  initialExpertId?: string;
  /** When set, modal is in edit mode: title "Edit Matter", button "Save Changes", form prefilled. */
  matter?: CreateMatterModalPrefillMatter;
  onSubmit: (data: CreateMatterFormData) => void | Promise<void>;
  isSubmitting?: boolean;
}

function getDefaultFormValues(
  initialExpertId?: string,
  matter?: CreateMatterModalPrefillMatter,
): CreateMatterFormData {
  if (matter) {
    const prefill = matter;
    const expertIdFromPrefill =
      getFirstHiredExpertId(prefill.matterExperts) ?? prefill.expertIds?.[0];
    return {
      expertId: expertIdFromPrefill ?? '',
      title: prefill.title,
      caseDescription: prefill.caseDescription || prefill.description || '',
      caseNumber: prefill.caseNumber ?? '',
      courtName: prefill.courtName ?? prefill.court ?? '',
      location: prefill.location ?? '',
      keyDates: prefill.keyDates ?? '',
      nextDeadline: toHtmlDate(prefill.nextDeadline),
      travelPlans: prefill.travelPlans ?? '',
      writtenReportNeeded: prefill.writtenReportNeeded ?? '',
      side: (prefill.side ?? '') as MatterSide | '',
    };
  }
  return {
    expertId: initialExpertId ?? '',
    title: '',
    caseDescription: '',
    caseNumber: '',
    courtName: '',
    location: '',
    keyDates: '',
    nextDeadline: '',
    travelPlans: '' as const,
    writtenReportNeeded: '' as const,
    side: '' as const,
  };
}

const CreateMatterModal = ({
  isOpen,
  onClose,
  experts,
  initialExpertId,
  matter,
  onSubmit,
  isSubmitting = false,
}: Props) => {
  const isEditMode = Boolean(matter);
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<CreateMatterFormData>({
    defaultValues: getDefaultFormValues(initialExpertId, matter),
  });

  const resetKey = isOpen
    ? `open-${matter?.id ?? ''}-${initialExpertId ?? ''}`
    : 'closed';
  const prevResetKey = useRef(resetKey);
  if (isOpen && prevResetKey.current !== resetKey) {
    prevResetKey.current = resetKey;
    reset(getDefaultFormValues(initialExpertId, matter));
  }
  if (!isOpen) {
    prevResetKey.current = 'closed';
  }

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const validateAndSubmit = async (data: CreateMatterFormData) => {
    const result = schema.safeParse(data);
    if (!result.success) {
      for (const err of result.error.errors) {
        const path = err.path[0] as keyof CreateMatterFormData;
        if (path) {
          setError(path, { message: err.message });
        }
      }
      return;
    }
    try {
      await onSubmit(result.data);
      handleClose();
    } catch {
      // onSubmit may show a toast and rethrow; skipping handleClose keeps the dialog open.
    }
  };

  const handleClose = () => {
    reset(getDefaultFormValues(initialExpertId, undefined));
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperComponent={StyledDialogPaper}
    >
      <MobileHeaderShrink>
        <ModalHeader
          title={isEditMode ? 'Edit Matter' : 'Create New Matter'}
          subtitle={getMatterSubtitle({
            isEditMode,
            matter,
            initialExpertId,
            experts,
          })}
          onClose={handleClose}
        />
      </MobileHeaderShrink>
      <StyledDialogContent>
        <FormWrapper id="create-matter-form">
          <Stack gap={{ xs: 3.5, sm: 2.5 }}>
            {!isEditMode && initialExpertId && (
              <Controller
                name="expertId"
                control={control}
                render={({ field }) => (
                  <input {...field} type="hidden" value={initialExpertId} />
                )}
              />
            )}
            <SectionLabel
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
            >
              Matter details
            </SectionLabel>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Matter Title"
                  required
                  placeholder="e.g., Smith v. Johnson Medical Review"
                  error={Boolean(errors.title)}
                  helperText={errors.title?.message}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
            <Controller
              name="caseDescription"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Brief case description"
                  required
                  placeholder="e.g. Need forensic pathology review to opine on conscious pain and suffering in 72 yo M decedent with head trauma from pedestrian v. car collision."
                  multiline
                  rows={4}
                  error={Boolean(errors.caseDescription)}
                  helperText={errors.caseDescription?.message}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
            <FormGrid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="caseNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Case number (Optional)"
                      placeholder="e.g. 1:24-cv-00123"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="courtName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Court (Optional)"
                      placeholder="e.g. Superior Court of California"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Location (Optional)"
                      placeholder="e.g. San Francisco, CA or venue / jurisdiction"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="keyDates"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Key Dates (Optional)"
                      placeholder="e.g. Discovery cutoff: Mar 1, 2025; Trial: May 15, 2025"
                      multiline
                      rows={2}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="nextDeadline"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Next deadline / court date (Optional)"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </FormGrid>

            <SectionLabelWithTop
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
            >
              Case context
            </SectionLabelWithTop>
            <FormGrid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="travelPlans"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel id="create-matter-travel-label" shrink>
                        Travel Plans (optional)
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="create-matter-travel-label"
                        label="Travel Plans (optional)"
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Not specified</em>
                        </MenuItem>
                        {Object.values(TravelPlans).map((value) => (
                          <MenuItem key={value} value={value}>
                            {travelPlansLabel[value]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="writtenReportNeeded"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel id="create-matter-report-label" shrink>
                        Written report? (optional)
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="create-matter-report-label"
                        label="Written report? (optional)"
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Not specified</em>
                        </MenuItem>
                        {Object.values(WrittenReportNeeded).map((value) => (
                          <MenuItem key={value} value={value}>
                            {writtenReportNeededLabel[value]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </FormGrid>
          </Stack>
        </FormWrapper>
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledSubmitButton
          variant="contained"
          onClick={handleSubmit(validateAndSubmit)}
          form="create-matter-form"
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {getSubmitLabel({ isEditMode, isSubmitting })}
        </StyledSubmitButton>
      </StyledDialogActions>
    </Dialog>
  );
};

const StyledDialogPaper = styled(Paper)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    margin: 0,
    maxWidth: '100%',
    width: '100%',
    maxHeight: '100vh',
    height: '100vh',
    borderRadius: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  paddingTop: 0,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  maxHeight: '70vh',
  overflowY: 'auto',
  [theme.breakpoints.down('sm')]: {
    flex: '1 1 0',
    minHeight: 0,
    maxHeight: 'none',
    overflowY: 'auto',
  },
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

const FormWrapper = styled('form')(() => ({
  marginTop: 0,
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const SectionLabelWithTop = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    borderTop: '1px solid',
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
  },
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

const MobileHeaderShrink = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  [theme.breakpoints.up('sm')]: {
    display: 'contents',
  },
}));

const StyledSubmitButton = styled(Button)(({ theme }) => ({
  minHeight: 44,
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
  backgroundColor: theme.palette.darkNavy,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.amount.main,
  },
}));

const FormGrid = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& > .MuiGrid-root:not(.MuiGrid-container)': {
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
  },
}));

export { CreateMatterModal };
